import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Loader2, ArrowLeft, CheckCircle2, XCircle, Clock, FileText, Edit, Download, AlertTriangle, Paperclip } from 'lucide-react';
import { getGasReadingWarnings, isGasReadingFieldUnsafe, orderChecklistByStatus } from '../../utils/ptwValidation';
import { formatDateTime } from '../../utils/dateUtils';
import PTWWorkflowActions from '../../components/PTWWorkflowActions';
import PTWTimeline from '../../components/PTWTimeline';
import { AuditTrailViewer } from '../../components/AuditTrail';
import { useAlert } from '../../context/AlertContext';
import { usePermissions } from '../../context/PermissionContext';
import { useSite } from '../../context/SiteContext';
import TabNavigation from '../../components/TabNavigation';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const PTWDetail = ({ user, onLogout }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const { hasPermission } = usePermissions();
  const { showSuccess, showError, showWarning, showInfo, confirm } = useAlert();
  const [permit, setPermit] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [exporting, setExporting] = useState(false);
  const [activeTab, setActiveTab] = useState('details'); // 'details' or 'audit'
  const [attachments, setAttachments] = useState([]);
  const { activeSiteId } = useSite();
  const isCrossSiteReadOnly = permit?.read_only === true;

  useEffect(() => {
    fetchPermit();
  }, [id]);

  const fetchPermit = async () => {
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permits/${id}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      setPermit(response.data);

      // Fetch attachments
      try {
        const attachRes = await axios.get(`${API}/permits/${id}/attachments`, {
          headers: { Authorization: `Bearer ${token}` }
        });
        setAttachments(attachRes.data?.attachments || []);
      } catch (attachErr) {
        console.error('Failed to fetch attachments:', attachErr);
      }

      setLoading(false);
    } catch (err) {
      setError(err.response?.data?.detail || 'Failed to load permit');
      setLoading(false);
    }
  };

  const handleExportPDF = async () => {
    setExporting(true);
    try {
      const token = localStorage.getItem('token');
      const response = await axios.get(`${API}/permits/${id}/export-pdf`, {
        headers: { Authorization: `Bearer ${token}` },
        responseType: 'blob'
      });
      
      // Create download link
      const url = window.URL.createObjectURL(new Blob([response.data]));
      const link = document.createElement('a');
      link.href = url;
      const filename = `PTW_${permit.permit_number.replace('/', '-')}_${new Date().toISOString().split('T')[0]}.pdf`;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
      window.URL.revokeObjectURL(url);
      
      setExporting(false);
    } catch (err) {
      console.error('Export failed:', err);
      showError('Failed to export PDF. Please try again.');
      setExporting(false);
    }
  };

  const handleDownloadAttachment = async (fileId, filename) => {
    try {
      const token = localStorage.getItem('token');
      const res = await axios.get(`${API}/files/${fileId}/download`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const link = document.createElement('a');
      link.href = res.data.download_url;
      link.setAttribute('download', filename);
      document.body.appendChild(link);
      link.click();
      link.remove();
    } catch (err) {
      console.error('Download failed:', err);
      showError('Failed to download file');
    }
  };

  const formatFileSize = (bytes) => {
    if (!bytes) return '0 B';
    if (bytes < 1024) return `${bytes} B`;
    if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
    return `${(bytes / (1024 * 1024)).toFixed(1)} MB`;
  };

  const getFileIcon = (contentType) => {
    if (contentType?.includes('pdf')) return '📄';
    if (contentType?.includes('word') || contentType?.includes('msword')) return '📝';
    if (contentType?.includes('sheet') || contentType?.includes('excel')) return '📊';
    return '📎';
  };

  const getStatusColor = (status) => {
    const colors = {
      'Draft': 'bg-gray-100 text-gray-800',
      'Pending Approval': 'bg-yellow-100 text-yellow-800',
      'Approved': 'bg-blue-100 text-blue-800',
      'Active': 'bg-green-100 text-green-800',
      'Suspended': 'bg-red-100 text-red-800',
      'Cancelled': 'bg-red-200 text-red-900',
      'Closed': 'bg-purple-100 text-purple-800',
      'Rejected': 'bg-red-100 text-red-800',
      'Expired': 'bg-gray-100 text-gray-800',
      'Extension Requested': 'bg-orange-100 text-orange-800',
      'Under Observation': 'bg-amber-100 text-amber-800',
      'HSE Verified': 'bg-green-100 text-green-800'
    };
    return colors[status] || 'bg-gray-100 text-gray-800';
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-screen">
        <Loader2 className="w-8 h-8 animate-spin text-blue-600" />
      </div>
    );
  }

  if (error || !permit) {
    return (
      <div className="min-h-screen bg-gray-50 p-6">
        <div className="max-w-5xl mx-auto">
          <Alert variant="destructive">
            <AlertDescription>{error || 'Permit not found'}</AlertDescription>
          </Alert>
          <Button onClick={() => navigate('/ptw')} className="mt-4">
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-50">
      <TabNavigation user={user} onLogout={onLogout} />
      <div className="p-6">
      <div className="max-w-5xl mx-auto">
        {/* Header */}
        <div className="mb-6">
          <Button
            variant="outline"
            onClick={() => navigate('/ptw')}
            className="mb-4"
          >
            <ArrowLeft className="w-4 h-4 mr-2" />
            Back to List
          </Button>
          
          {isCrossSiteReadOnly && (
            <div className="bg-amber-50 border-l-4 border-amber-400 p-3 mb-4">
              <p className="text-sm text-amber-700">
                This record is from <strong>{permit.site_name}</strong> and is read-only.
              </p>
            </div>
          )}

          <div className="flex items-start justify-between">
            <div>
              <h1 className="text-3xl font-bold text-gray-900">{permit.permit_number}</h1>
              <div className="flex items-center gap-3 mt-1">
                <p className="text-gray-600">
                  {(Array.isArray(permit.permit_types) && permit.permit_types.length > 0
                    ? permit.permit_types.join(', ')
                    : permit.permit_type)} Permit
                </p>
                {permit.site_name && (
                  <span className="text-sm font-medium text-blue-700">Site: {permit.site_name}</span>
                )}
              </div>
            </div>
            <div className="flex items-center gap-3 flex-wrap">
              <span className={`px-4 py-2 rounded-full text-sm font-semibold ${getStatusColor(permit.status)}`}>
                {permit.status}
              </span>
              <Button
                onClick={handleExportPDF}
                disabled={exporting}
                className="bg-green-600 hover:bg-green-700"
              >
                {exporting ? (
                  <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                ) : (
                  <Download className="w-4 h-4 mr-2" />
                )}
                Export PDF
              </Button>
              {permit.status === 'Draft' && (String(permit.created_by) === String(user?.id || user?.user_id) || hasPermission('ptw', 'update')) && !isCrossSiteReadOnly && (
                <Button
                  onClick={() => navigate(`/ptw/edit/${permit.id}`)}
                  className="bg-blue-600 hover:bg-blue-700"
                >
                  <Edit className="w-4 h-4 mr-2" />
                  Edit
                </Button>
              )}
            </div>
          </div>

          {permit.status === 'Pending Approval' && permit.approvals && permit.approvals.length > 0 && (
            <div className="mt-4 flex items-center gap-2 px-4 py-3 bg-yellow-50 border border-yellow-200 rounded-lg">
              <Clock className="w-5 h-5 text-yellow-600" />
              <span className="text-yellow-800">
                <span className="font-semibold">Pending with: </span>
                {permit.approvals[permit.current_approval_level || 0]?.approver_name}
                {permit.approvals[permit.current_approval_level || 0]?.approver_role &&
                  ` (${permit.approvals[permit.current_approval_level || 0].approver_role})`
                }
              </span>
            </div>
          )}
        </div>

        {/* Workflow Actions */}
        <div className="mb-6">
          <PTWWorkflowActions permit={permit} onUpdate={fetchPermit} />
        </div>

        {/* Tab Navigation */}
        <div className="border-b border-gray-200 mb-6">
          <nav className="-mb-px flex space-x-8" aria-label="Tabs">
            <button
              onClick={() => setActiveTab('details')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'details'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Details
            </button>
            <button
              onClick={() => setActiveTab('audit')}
              className={`whitespace-nowrap py-3 px-1 border-b-2 font-medium text-sm ${
                activeTab === 'audit'
                  ? 'border-blue-600 text-blue-600'
                  : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
              }`}
            >
              Audit History
            </button>
          </nav>
        </div>

        {/* Tab Content */}
        {activeTab === 'details' ? (
          <>
        {/* Main Details Card */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Permit Details</CardTitle>
          </CardHeader>
          <CardContent>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div>
                <label className="text-sm font-semibold text-gray-600">Work Description</label>
                <p className="text-gray-900 mt-1">{permit.work_description}</p>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-600">Location</label>
                <p className="text-gray-900 mt-1">{permit.location}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600">Department</label>
                <p className="text-gray-900 mt-1">{permit.department || '—'}</p>
              </div>

              {permit.shift && (
                <div>
                  <label className="text-sm font-semibold text-gray-600">Shift</label>
                  <p className="text-gray-900 mt-1">{permit.shift}</p>
                </div>
              )}

              {(permit.esi_wc_covered === true || permit.esi_wc_covered === false) && (
                <div>
                  <label className="text-sm font-semibold text-gray-600">ESI / WC Covered</label>
                  <p className="text-gray-900 mt-1">
                    {permit.esi_wc_covered ? 'Yes' : 'No'}
                    {permit.esi_wc_covered === false && permit.esi_wc_approved_by ? ` · Plant Head: ${permit.esi_wc_approved_by}` : ''}
                  </p>
                </div>
              )}

              <div>
                <label className="text-sm font-semibold text-gray-600">Applicant</label>
                <p className="text-gray-900 mt-1">{permit.applicant_name}</p>
              </div>
              
              <div>
                <label className="text-sm font-semibold text-gray-600">Work Permit Valid From</label>
                <p className="text-gray-900 mt-1">{formatDateTime(permit.work_start_date)}</p>
              </div>

              <div>
                <label className="text-sm font-semibold text-gray-600">Work Permit Valid To</label>
                <p className="text-gray-900 mt-1">{formatDateTime(permit.work_end_date)}</p>
              </div>

              {permit.extension_requested && permit.extension_approved_date && permit.extension_end_date && (
                <div className="col-span-1 md:col-span-2">
                  <div className="flex items-center gap-2 px-3 py-2 bg-yellow-50 border border-yellow-300 rounded-lg">
                    <span className="text-yellow-700 font-semibold text-sm">Work Permit Extended To:</span>
                    <span className="text-yellow-900 font-bold text-sm">{formatDateTime(permit.extension_end_date)}</span>
                  </div>
                </div>
              )}

              {permit.contractor_company && (
                <div>
                  <label className="text-sm font-semibold text-gray-600">Contractor</label>
                  <p className="text-gray-900 mt-1">{permit.contractor_company}</p>
                </div>
              )}

              <div>
                <label className="text-sm font-semibold text-gray-600">Created</label>
                <p className="text-gray-900 mt-1">
                  {formatDateTime(permit.created_at)}
                </p>
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Permit Acceptance, Authorization & Hand-over (Safe Work Permit format) */}
        {(permit.contractor_supervisor_name || permit.safety_officer_name ||
          (permit.authorizations && permit.authorizations.length > 0) ||
          (permit.shift_handover && (permit.shift_handover.handed_over_by || permit.shift_handover.accepted_by))) && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Acceptance, Authorization &amp; Hand-over</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {(permit.contractor_supervisor_name || permit.safety_officer_name) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  {permit.contractor_supervisor_name && (
                    <div><span className="text-gray-600">Contractor Supervisor:</span> <span className="font-semibold">{permit.contractor_supervisor_name}</span>{permit.contractor_supervisor_mobile ? ` · ${permit.contractor_supervisor_mobile}` : ''}</div>
                  )}
                  {permit.safety_officer_name && (
                    <div><span className="text-gray-600">Safety Officer:</span> <span className="font-semibold">{permit.safety_officer_name}</span>{permit.safety_officer_mobile ? ` · ${permit.safety_officer_mobile}` : ''}</div>
                  )}
                </div>
              )}
              {permit.authorizations && permit.authorizations.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Authorization</p>
                  <div className="flex flex-wrap gap-2 text-sm">
                    {permit.authorizations.filter(a => a.name).map((a, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-50 text-blue-800 rounded"><b>{a.role}:</b> {a.name}{a.authorized_at ? ` (${a.authorized_at})` : ''}</span>
                    ))}
                  </div>
                </div>
              )}
              {permit.shift_handover && (permit.shift_handover.handed_over_by || permit.shift_handover.accepted_by) && (
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4 text-sm">
                  <div><span className="text-gray-600">Handed over by:</span> <span className="font-semibold">{permit.shift_handover.handed_over_by || '—'}</span>{permit.shift_handover.handed_over_mobile ? ` · ${permit.shift_handover.handed_over_mobile}` : ''}</div>
                  <div><span className="text-gray-600">Accepted by:</span> <span className="font-semibold">{permit.shift_handover.accepted_by || '—'}</span>{permit.shift_handover.accepted_mobile ? ` · ${permit.shift_handover.accepted_mobile}` : ''}</div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Hot Work post-work observation & physical inspection */}
        {(permit.observation_started_at || permit.post_work_inspection) && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Post-Work Observation &amp; Inspection</CardTitle></CardHeader>
            <CardContent className="space-y-3 text-sm">
              {permit.observation_started_at && (
                <p><span className="text-gray-600">Observation started:</span> <span className="font-semibold">{formatDateTime(permit.observation_started_at)}</span> <span className="text-gray-500">({permit.observation_minutes_required || 30}-minute fire watch)</span></p>
              )}
              {permit.post_work_inspection ? (
                <div>
                  <p className="text-gray-600 mb-1">Physical inspection confirmed by <span className="font-semibold">{permit.post_work_inspection.inspected_by_name || '—'}</span>{permit.post_work_inspection.inspected_at ? ` on ${formatDateTime(permit.post_work_inspection.inspected_at)}` : ''}:</p>
                  <ul className="space-y-1">
                    {[['no_fire_smoke_hotspots','No fire, smoke, or hot spots'],['housekeeping_done','Housekeeping completed'],['fire_equipment_restored','Fire equipment restored'],['area_safe','Area safe for normal operations']].map(([k,label]) => (
                      <li key={k} className="flex items-center gap-2">
                        {permit.post_work_inspection[k] ? <CheckCircle2 className="w-4 h-4 text-green-600" /> : <XCircle className="w-4 h-4 text-gray-400" />}
                        <span className="text-gray-800">{label}</span>
                      </li>
                    ))}
                  </ul>
                  {permit.post_work_inspection.remarks && (
                    <p className="mt-2 text-gray-700"><span className="font-semibold">Remarks:</span> {permit.post_work_inspection.remarks}</p>
                  )}
                </div>
              ) : (
                <p className="text-amber-700">Awaiting physical inspection before final closure.</p>
              )}
            </CardContent>
          </Card>
        )}

        {/* PPE / Tool Box Talk / JSA (Safe Work Permit format) */}
        {((permit.ppe_required && permit.ppe_required.length > 0) || permit.ppe_fire_extinguisher_no || permit.ppe_locks_tags_no || permit.ppe_other ||
          (permit.tbt_attendees && permit.tbt_attendees.length > 0) || (permit.jsa && permit.jsa.length > 0)) && (
          <Card className="mb-6">
            <CardHeader><CardTitle>Safety Equipment, Tool Box Talk &amp; JSA</CardTitle></CardHeader>
            <CardContent className="space-y-5">
              {((permit.ppe_required && permit.ppe_required.length > 0) || permit.ppe_fire_extinguisher_no || permit.ppe_locks_tags_no || permit.ppe_other) && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">PPE / Safety Equipment</p>
                  <div className="flex flex-wrap gap-2">
                    {(permit.ppe_required || []).map((p, i) => (
                      <span key={i} className="px-2 py-1 bg-blue-50 text-blue-800 rounded text-xs">{p}</span>
                    ))}
                  </div>
                  <div className="text-sm text-gray-600 mt-2 space-x-4">
                    {permit.ppe_fire_extinguisher_no && <span><b>Fire Ext. No.:</b> {permit.ppe_fire_extinguisher_no}</span>}
                    {permit.ppe_locks_tags_no && <span><b>Locks/Tags:</b> {permit.ppe_locks_tags_no}</span>}
                    {permit.ppe_other && <span><b>Other:</b> {permit.ppe_other}</span>}
                  </div>
                </div>
              )}
              {permit.tbt_attendees && permit.tbt_attendees.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Tool Box Talk — Attendance</p>
                  <ul className="text-sm text-gray-800 space-y-1">
                    {permit.tbt_attendees.map((a, i) => (
                      <li key={i}>{i + 1}. {a.name}{a.job_profile ? ` — ${a.job_profile}` : ''}</li>
                    ))}
                  </ul>
                </div>
              )}
              {permit.jsa && permit.jsa.length > 0 && (
                <div>
                  <p className="text-sm font-semibold text-gray-700 mb-2">Job Safety Analysis</p>
                  <div className="hidden md:block overflow-x-auto">
                    <table className="w-full text-sm border border-gray-200">
                      <thead><tr className="bg-gray-50 text-left text-gray-600">
                        <th className="p-2 border border-gray-200">Step</th>
                        <th className="p-2 border border-gray-200">Hazard</th>
                        <th className="p-2 border border-gray-200">Control</th>
                      </tr></thead>
                      <tbody>
                        {permit.jsa.map((j, i) => (
                          <tr key={i}>
                            <td className="p-2 border border-gray-200">{j.step || '—'}</td>
                            <td className="p-2 border border-gray-200">{j.hazard || '—'}</td>
                            <td className="p-2 border border-gray-200">{j.control || '—'}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>

                  {/* Mobile: one labelled block per JSA row */}
                  <div className="md:hidden space-y-3">
                    {permit.jsa.map((j, i) => (
                      <div key={i} className="border border-gray-200 rounded-lg p-3 text-sm space-y-1">
                        <div><span className="font-semibold text-gray-600">Step:</span> {j.step || '—'}</div>
                        <div><span className="font-semibold text-gray-600">Hazard:</span> {j.hazard || '—'}</div>
                        <div><span className="font-semibold text-gray-600">Control:</span> {j.control || '—'}</div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Hazards & Controls */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-6 mb-6">
          <Card>
            <CardHeader>
              <CardTitle className="text-red-700">Identified Hazards</CardTitle>
            </CardHeader>
            <CardContent>
              {permit.hazards_identified && permit.hazards_identified.length > 0 ? (
                <ul className="space-y-2">
                  {permit.hazards_identified.map((hazard, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-red-600 mt-1">⚠️</span>
                      <span className="text-gray-900">{hazard}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No hazards identified</p>
              )}
            </CardContent>
          </Card>

          <Card>
            <CardHeader>
              <CardTitle className="text-green-700">Control Measures</CardTitle>
            </CardHeader>
            <CardContent>
              {permit.control_measures && permit.control_measures.length > 0 ? (
                <ul className="space-y-2">
                  {permit.control_measures.map((control, index) => (
                    <li key={index} className="flex items-start gap-2">
                      <span className="text-green-600 mt-1">✓</span>
                      <span className="text-gray-900">{control}</span>
                    </li>
                  ))}
                </ul>
              ) : (
                <p className="text-gray-500">No control measures specified</p>
              )}
            </CardContent>
          </Card>
        </div>

        {/* Safety Checklist */}
        <Card className="mb-6">
          <CardHeader>
            <CardTitle>Safety Checklist</CardTitle>
          </CardHeader>
          <CardContent>
            {permit.safety_checklist && permit.safety_checklist.length > 0 ? (
              <div className="space-y-3">
                {orderChecklistByStatus(permit.safety_checklist).map((item, index, arr) => {
                  const groupLabel = item.is_custom ? 'Custom Checkpoints' : (item.permit_type || '');
                  const prev = arr[index - 1];
                  const prevLabel = prev ? (prev.is_custom ? 'Custom Checkpoints' : (prev.permit_type || '')) : null;
                  const showHeader = groupLabel && groupLabel !== prevLabel;
                  return (
                  <React.Fragment key={index}>
                  {showHeader && (
                    <h4 className="text-sm font-bold text-gray-700 mt-4 pb-1 border-b border-gray-200">{groupLabel}</h4>
                  )}
                  <div className="flex items-start gap-3 p-3 bg-gray-50 rounded-lg">
                    {(() => {
                      const st = item.status || (item.checked ? 'yes' : null);
                      if (st === 'yes') return <CheckCircle2 className="w-5 h-5 text-green-600 mt-0.5" />;
                      if (st === 'no') return <XCircle className="w-5 h-5 text-red-600 mt-0.5" />;
                      if (st === 'na') return <span className="text-xs font-bold text-gray-500 mt-0.5 border border-gray-400 rounded px-1">NA</span>;
                      return <XCircle className="w-5 h-5 text-gray-400 mt-0.5" />;
                    })()}
                    <div className="flex-1">
                      <p className="font-semibold text-gray-900">
                        {item.question}
                      </p>
                      {item.remarks && (
                        <p className="text-sm text-gray-600 mt-1">
                          <span className="font-semibold">Remarks:</span> {item.remarks}
                        </p>
                      )}
                    </div>
                  </div>
                  </React.Fragment>
                  );
                })}
              </div>
            ) : (
              <p className="text-gray-500">No checklist items</p>
            )}
          </CardContent>
        </Card>

        {/* Workers */}
        {permit.workers && permit.workers.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Authorized Workers</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-3">
                {permit.workers.map((worker, index) => {
                  const namedCerts = (worker.certifications || []).filter(c => (c.name || '').trim());
                  return (
                  <div
                    key={index}
                    className={`p-4 rounded-lg border-2 ${
                      worker.has_valid_certifications
                        ? 'bg-green-50 border-green-200'
                        : worker.is_manual
                        ? 'bg-amber-50 border-amber-200'
                        : 'bg-red-50 border-red-200'
                    }`}
                  >
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="font-semibold text-gray-900 flex items-center gap-2">
                          {worker.employee_name}
                          {worker.is_manual && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 rounded-full text-xs font-semibold">
                              {worker.contractor_worker_id ? 'Contractor' : 'External'}
                            </span>
                          )}
                        </p>
                        {worker.contractor_company && (
                          <p className="text-xs text-gray-600 mt-1">{worker.contractor_company}</p>
                        )}
                        {worker.is_manual && worker.has_valid_certifications && namedCerts.length > 0 && (
                          <p className="text-xs text-green-700 mt-1">Competency verified from provided certificate(s).</p>
                        )}
                        {worker.is_manual && worker.has_valid_certifications && namedCerts.length === 0 && (
                          <p className="text-xs text-gray-600 mt-1">No certifications required for this permit type.</p>
                        )}
                        {!worker.has_valid_certifications && worker.missing_certifications && worker.missing_certifications.length > 0 && (
                          <div className="mt-2">
                            <p className={`text-sm font-semibold ${worker.is_manual ? 'text-amber-700' : 'text-red-700'}`}>
                              {worker.is_manual ? 'Certificates required for:' : 'Missing Certifications:'}
                            </p>
                            <div className="flex flex-wrap gap-1 mt-1">
                              {worker.missing_certifications.map((cert, idx) => (
                                <span key={idx} className={`px-2 py-1 rounded text-xs ${worker.is_manual ? 'bg-amber-100 text-amber-800' : 'bg-red-100 text-red-800'}`}>
                                  {cert}
                                </span>
                              ))}
                            </div>
                          </div>
                        )}
                      </div>
                      {worker.has_valid_certifications ? (
                        <CheckCircle2 className="w-6 h-6 text-green-600" />
                      ) : worker.is_manual ? (
                        <AlertTriangle className="w-6 h-6 text-amber-600" />
                      ) : (
                        <XCircle className="w-6 h-6 text-red-600" />
                      )}
                    </div>
                    {worker.is_manual && namedCerts.length > 0 && (
                      <div className="mt-3 pt-3 border-t border-gray-200">
                        <p className="text-sm font-semibold text-gray-700 mb-1">Certificates:</p>
                        <ul className="text-sm text-gray-800 space-y-1">
                          {namedCerts.map((c, idx) => (
                            <li key={idx}>
                              <span className="font-medium">{c.name}</span>
                              {c.certificate_number ? ` · No. ${c.certificate_number}` : ''}
                              {c.issuing_authority ? ` · ${c.issuing_authority}` : ''}
                              {c.expiry_date ? ` · exp ${c.expiry_date}` : ''}
                            </li>
                          ))}
                        </ul>
                      </div>
                    )}
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Gas Testing Results */}
        {permit.gas_test_readings && permit.gas_test_readings.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Gas Testing Results</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {permit.gas_test_readings.map((reading, index) => {
                  const warnings = getGasReadingWarnings(reading);
                  const hasWarnings = warnings.length > 0;

                  return (
                  <div key={index} className={`p-4 rounded-lg ${hasWarnings ? 'bg-red-50 border-2 border-red-300' : 'bg-blue-50'}`}>
                    <div className="flex items-center justify-between mb-3">
                      <p className="font-semibold text-gray-900">
                        Reading #{index + 1}{reading.test_time ? ` - ${formatDateTime(reading.test_time)}` : ''}
                      </p>
                      {hasWarnings && (
                        <span className="flex items-center text-red-600 text-sm font-semibold">
                          <AlertTriangle className="w-4 h-4 mr-1" />
                          Unsafe Levels Detected
                        </span>
                      )}
                    </div>

                    {/* Warning messages */}
                    {hasWarnings && (
                      <div className="mb-4 p-3 bg-red-100 border border-red-300 rounded-lg">
                        <div className="flex items-start">
                          <AlertTriangle className="w-5 h-5 text-red-600 mr-2 flex-shrink-0 mt-0.5" />
                          <div>
                            <p className="font-bold text-red-800 text-sm mb-1">⚠️ WARNING: Gas readings outside safe limits!</p>
                            <ul className="text-red-700 text-sm space-y-1">
                              {warnings.map((warning, wIdx) => (
                                <li key={wIdx}>• {warning}</li>
                              ))}
                            </ul>
                          </div>
                        </div>
                      </div>
                    )}

                    <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">O₂ Level <span className="text-xs">(19.5-23.5%)</span>:</span>
                        <p className={`font-semibold ${isGasReadingFieldUnsafe(reading, 'oxygen_level') ? 'text-red-600' : 'text-gray-900'}`}>
                          {reading.oxygen_level !== '' && reading.oxygen_level != null ? `${reading.oxygen_level}%` : '—'}
                          {isGasReadingFieldUnsafe(reading, 'oxygen_level') && <AlertTriangle className="w-4 h-4 inline ml-1 text-red-600" />}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">LEL <span className="text-xs">(&lt;10%)</span>:</span>
                        <p className={`font-semibold ${isGasReadingFieldUnsafe(reading, 'lel_level') ? 'text-red-600' : 'text-gray-900'}`}>
                          {reading.lel_level !== '' && reading.lel_level != null ? `${reading.lel_level}%` : '—'}
                          {isGasReadingFieldUnsafe(reading, 'lel_level') && <AlertTriangle className="w-4 h-4 inline ml-1 text-red-600" />}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">H₂S <span className="text-xs">(&lt;10 ppm)</span>:</span>
                        <p className={`font-semibold ${isGasReadingFieldUnsafe(reading, 'h2s_level') ? 'text-red-600' : 'text-gray-900'}`}>
                          {reading.h2s_level !== '' && reading.h2s_level != null ? `${reading.h2s_level} ppm` : '—'}
                          {isGasReadingFieldUnsafe(reading, 'h2s_level') && <AlertTriangle className="w-4 h-4 inline ml-1 text-red-600" />}
                        </p>
                      </div>
                      <div>
                        <span className="text-gray-600">CO <span className="text-xs">(&lt;35 ppm)</span>:</span>
                        <p className={`font-semibold ${isGasReadingFieldUnsafe(reading, 'co_level') ? 'text-red-600' : 'text-gray-900'}`}>
                          {reading.co_level !== '' && reading.co_level != null ? `${reading.co_level} ppm` : '—'}
                          {isGasReadingFieldUnsafe(reading, 'co_level') && <AlertTriangle className="w-4 h-4 inline ml-1 text-red-600" />}
                        </p>
                      </div>
                    </div>
                    {Array.isArray(reading.custom_fields) && reading.custom_fields.filter(f => (f.label || '').trim()).length > 0 && (
                      <div className={`mt-3 pt-3 border-t ${hasWarnings ? 'border-red-200' : 'border-blue-200'}`}>
                        <p className="text-sm font-semibold text-gray-700 mb-1">Additional Readings:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          {reading.custom_fields.filter(f => (f.label || '').trim()).map((f, fIdx) => (
                            <div key={fIdx}>
                              <span className="text-gray-600">{f.label}:</span>
                              <p className="font-semibold text-gray-900">{f.value || '—'}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    <div className={`mt-3 pt-3 border-t ${hasWarnings ? 'border-red-200' : 'border-blue-200'}`}>
                      <p className="text-sm text-gray-700">
                        <span className="font-semibold">Tested by:</span> {reading.tester_name || '—'}
                      </p>
                      {reading.remarks && (
                        <p className="text-sm text-gray-700 mt-1">
                          <span className="font-semibold">Remarks:</span> {reading.remarks}
                        </p>
                      )}
                    </div>
                  </div>
                  );
                })}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Isolation Points */}
        {permit.isolation_points && permit.isolation_points.length > 0 && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Isolation/LOTO Points</CardTitle>
            </CardHeader>
            <CardContent>
              <div className="space-y-4">
                {permit.isolation_points.map((point, index) => (
                  <div key={index} className="p-4 bg-orange-50 rounded-lg">
                    <p className="font-semibold text-gray-900 mb-3">
                      Isolation Point #{index + 1}
                    </p>
                    <div className="grid grid-cols-2 gap-4 text-sm">
                      <div>
                        <span className="text-gray-600">Equipment Tag:</span>
                        <p className="font-semibold text-gray-900">{point.equipment_tag}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Isolation Type:</span>
                        <p className="font-semibold text-gray-900">{point.isolation_type}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Lock Number:</span>
                        <p className="font-semibold text-gray-900">{point.lock_number}</p>
                      </div>
                      <div>
                        <span className="text-gray-600">Verified By:</span>
                        <p className="font-semibold text-gray-900">{point.isolation_verified_by}</p>
                      </div>
                    </div>
                    {Array.isArray(point.custom_fields) && point.custom_fields.filter(f => (f.label || '').trim()).length > 0 && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <p className="text-sm font-semibold text-gray-700 mb-1">Additional Fields:</p>
                        <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-sm">
                          {point.custom_fields.filter(f => (f.label || '').trim()).map((f, fIdx) => (
                            <div key={fIdx}>
                              <span className="text-gray-600">{f.label}:</span>
                              <p className="font-semibold text-gray-900">{f.value || '—'}</p>
                            </div>
                          ))}
                        </div>
                      </div>
                    )}
                    {point.remarks && (
                      <div className="mt-3 pt-3 border-t border-orange-200">
                        <p className="text-sm text-gray-700">
                          <span className="font-semibold">Remarks:</span> {point.remarks}
                        </p>
                      </div>
                    )}
                  </div>
                ))}
              </div>
            </CardContent>
          </Card>
        )}

        {/* Emergency Contacts */}
        {(permit.emergency_contact_name || permit.rescue_plan_available) && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle>Emergency Information</CardTitle>
            </CardHeader>
            <CardContent>
              {permit.emergency_contact_name && (
                <div className="mb-4">
                  <p className="text-sm font-semibold text-gray-600">Emergency Contact</p>
                  <p className="text-gray-900 mt-1">
                    {permit.emergency_contact_name}
                    {permit.emergency_contact_number && ` - ${permit.emergency_contact_number}`}
                  </p>
                </div>
              )}
              
              {permit.rescue_plan_available && (
                <div>
                  <p className="text-sm font-semibold text-gray-600">Rescue Plan</p>
                  <div className="flex items-center gap-2 mt-1">
                    <CheckCircle2 className="w-5 h-5 text-green-600" />
                    <span className="text-gray-900">Rescue plan available</span>
                  </div>
                  {permit.rescue_plan_description && (
                    <p className="text-gray-700 mt-2 p-3 bg-gray-50 rounded">
                      {permit.rescue_plan_description}
                    </p>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Attachments */}
        {(attachments.length > 0 || permit.method_statement_url || permit.risk_assessment_url) && (
          <Card className="mb-6">
            <CardHeader>
              <CardTitle className="flex items-center gap-2">
                <Paperclip className="w-5 h-5" />
                Attachments
                {attachments.length > 0 && (
                  <span className="text-sm font-normal text-gray-500">({attachments.length})</span>
                )}
              </CardTitle>
            </CardHeader>
            <CardContent>
              {attachments.length > 0 && (
                <div className="space-y-3">
                  {attachments.map((file) => (
                    <div key={file.id} className="flex items-center gap-3 p-3 bg-gray-50 rounded-lg border border-gray-200">
                      <span className="text-lg flex-shrink-0">{getFileIcon(file.content_type)}</span>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-semibold text-gray-900 truncate">{file.filename}</p>
                        <p className="text-xs text-gray-500">
                          {formatFileSize(file.file_size)}
                          {file.uploaded_by && ` · Uploaded by ${file.uploaded_by}`}
                          {file.uploaded_at && ` · ${formatDateTime(file.uploaded_at)}`}
                        </p>
                      </div>
                      <Button
                        variant="outline"
                        size="sm"
                        onClick={() => handleDownloadAttachment(file.id, file.filename)}
                        className="flex-shrink-0"
                      >
                        <Download className="w-4 h-4 mr-1" />
                        Download
                      </Button>
                    </div>
                  ))}
                </div>
              )}

              {/* Legacy URLs for older permits */}
              {(permit.method_statement_url || permit.risk_assessment_url) && (
                <div className={attachments.length > 0 ? 'mt-4 pt-4 border-t border-gray-200' : ''}>
                  <p className="text-xs text-gray-500 mb-2 font-semibold">Legacy Documents</p>
                  {permit.method_statement_url && (
                    <div className="flex items-center gap-2 text-sm text-blue-600 mb-1">
                      <FileText className="w-4 h-4" />
                      <a href={permit.method_statement_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        Method Statement
                      </a>
                    </div>
                  )}
                  {permit.risk_assessment_url && (
                    <div className="flex items-center gap-2 text-sm text-blue-600">
                      <FileText className="w-4 h-4" />
                      <a href={permit.risk_assessment_url} target="_blank" rel="noopener noreferrer" className="hover:underline">
                        Risk Assessment
                      </a>
                    </div>
                  )}
                </div>
              )}
            </CardContent>
          </Card>
        )}

        {/* Status Timeline */}
        <PTWTimeline permit={permit} />
          </>
        ) : (
          /* Audit History Tab */
          <Card>
            <CardContent className="pt-6">
              <AuditTrailViewer
                entityType="permit"
                entityId={id}
                title="Permit Audit History"
              />
            </CardContent>
          </Card>
        )}
      </div>
    </div>
    </div>
  );
};

export default PTWDetail;
